import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { RefreshCw, Phone, MessageCircle, Mail, CheckCircle2, Inbox, FileSpreadsheet, FileText } from "lucide-react";
import { toast } from "sonner";

export default function Inquiries() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("new");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/inquiries", { params: { status: filter } });
      setRows(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const mark = async (id, status) => {
    try {
      await api.patch(`/inquiries/${id}`, { status });
      toast.success(`Marked as ${status}`);
      await load();
    } catch { toast.error("Failed to update"); }
  };

  const downloadFile = async (format) => {
    if (rows.length === 0) return toast.info("Nothing to export in this list");
    const path = format === "pdf" ? "/inquiries/export-pdf" : "/inquiries/export";
    const ext = format === "pdf" ? "pdf" : "xlsx";
    const mime = format === "pdf" ? "application/pdf" : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
    try {
      const res = await api.get(path, { params: { status: filter }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: mime }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `inquiries-${filter}-${new Date().toISOString().slice(0, 10)}.${ext}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Inquiries ${format.toUpperCase()} downloaded`);
    } catch {
      toast.error(`Failed to download ${format.toUpperCase()}`);
    }
  };

  const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleString(undefined, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
    catch { return iso; }
  };

  return (
    <div className="space-y-4" data-testid="inquiries-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display font-black text-3xl tracking-tight text-slate-900">Website Inquiries</h1>
          <p className="text-xs text-slate-500 mt-0.5">Leads submitted via the public contact form.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {["new", "contacted", "closed", "all"].map((s) => (
            <button key={s} onClick={() => setFilter(s)} className={`text-[11px] uppercase tracking-wider font-bold px-3 py-1.5 rounded-md border transition ${filter === s ? "bg-[color:var(--brand-navy)] text-white border-[color:var(--brand-navy)]" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"}`} data-testid={`filter-${s}`}>
              {s}
            </button>
          ))}
          <Button onClick={() => downloadFile("xlsx")} className="h-8 bg-[color:var(--brand-teal)] hover:bg-[color:var(--brand-teal-hover)] text-white text-xs font-semibold" data-testid="inquiries-excel-btn">
            <FileSpreadsheet className="h-3.5 w-3.5 mr-1.5" /> Excel
          </Button>
          <Button onClick={() => downloadFile("pdf")} className="h-8 bg-red-500 hover:bg-red-600 text-white text-xs font-semibold" data-testid="inquiries-pdf-btn">
            <FileText className="h-3.5 w-3.5 mr-1.5" /> PDF
          </Button>
          <Button onClick={load} disabled={loading} className="h-8 bg-[#2E77E6] hover:bg-[#2361C4] text-white text-xs font-semibold" data-testid="inquiries-refresh">
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        {rows.length === 0 ? (
          <div className="py-16 text-center text-slate-400 flex flex-col items-center gap-2">
            <Inbox className="h-10 w-10" />
            <div className="text-sm font-semibold">No inquiries in this list</div>
          </div>
        ) : (
          <table className="w-full text-sm crm-table" data-testid="inquiries-table">
            <thead className="bg-slate-50 text-slate-600 text-[10px] uppercase tracking-wider">
              <tr>
                <th className="px-3 py-2.5 text-left font-semibold">Received</th>
                <th className="px-3 py-2.5 text-left font-semibold">Name</th>
                <th className="px-3 py-2.5 text-left font-semibold">Mobile</th>
                <th className="px-3 py-2.5 text-left font-semibold">Email</th>
                <th className="px-3 py-2.5 text-left font-semibold">Service</th>
                <th className="px-3 py-2.5 text-left font-semibold">Message</th>
                <th className="px-3 py-2.5 text-left font-semibold">Status</th>
                <th className="px-3 py-2.5 text-right font-semibold w-56">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => (
                <tr key={r.id} data-testid={`inquiry-row-${r.id}`}>
                  <td className="px-3 py-2 font-mono-num text-slate-600 text-xs">{fmtDate(r.created_at)}</td>
                  <td className="px-3 py-2 font-semibold text-slate-800">{r.name}</td>
                  <td className="px-3 py-2 font-mono-num text-slate-700">{r.mobile}</td>
                  <td className="px-3 py-2 text-slate-600 text-xs">{r.email || "—"}</td>
                  <td className="px-3 py-2 text-slate-600 text-xs">{r.service || "—"}</td>
                  <td className="px-3 py-2 text-slate-600 text-xs max-w-xs truncate" title={r.message}>{r.message || "—"}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex px-2 py-0.5 rounded text-[10px] font-bold uppercase border ${
                      r.status === "new" ? "bg-amber-50 text-amber-700 border-amber-200" :
                      r.status === "contacted" ? "bg-blue-50 text-blue-700 border-blue-200" :
                      "bg-slate-100 text-slate-600 border-slate-200"
                    }`}>{r.status}</span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex items-center gap-1">
                      <a href={`tel:${r.mobile}`} className="p-1.5 rounded hover:bg-emerald-50 text-emerald-700" title="Call"><Phone className="h-3.5 w-3.5" /></a>
                      <a href={`https://wa.me/91${r.mobile}`} target="_blank" rel="noreferrer" className="p-1.5 rounded hover:bg-emerald-50 text-[#25D366]" title="WhatsApp"><MessageCircle className="h-3.5 w-3.5" /></a>
                      {r.email && <a href={`mailto:${r.email}`} className="p-1.5 rounded hover:bg-slate-100 text-slate-700" title="Email"><Mail className="h-3.5 w-3.5" /></a>}
                      {r.status !== "contacted" && (
                        <button onClick={() => mark(r.id, "contacted")} className="ml-1 px-2 py-1 rounded bg-blue-600 hover:bg-blue-700 text-white text-[10px] font-bold uppercase" data-testid={`mark-contacted-${r.id}`}>Contacted</button>
                      )}
                      {r.status !== "closed" && (
                        <button onClick={() => mark(r.id, "closed")} className="px-2 py-1 rounded bg-slate-600 hover:bg-slate-700 text-white text-[10px] font-bold uppercase" data-testid={`mark-closed-${r.id}`}>Close</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
